"use client";

import { FC, HTMLAttributes, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";

import TButton from "@/components/atoms/buttons/TButton";
import CTAButton from "@/components/atoms/buttons/CTAButton";

import logo from "@/assets/images/branding/logo.svg";
import MegaMenuDropdown, {
  ICategory,
} from "@/components/layouts/header/MegaMenuDropdown";
import MenuDropdown from "@/components/layouts/header/MenuDropdown";
import { IMenuDownloadBook } from "@/sections/common/MenuDownloadSection";

// -----------------------------------------------------------------------------------------

export type IHeaderSingleItem = {
  label: string;
  href: string;
};

export type IHeaderMenuItemstype = ICategory & {
  label?: string;
  href?: string;
  items?: IHeaderSingleItem[];
  header?: boolean;
  footer?: boolean;
};

export type IHeaderMenuItem = Omit<IHeaderSingleItem, "href"> & {
  href?: string;
  items?: IHeaderMenuItemstype[];
  options?: {
    width?: "full" | "fit";
    borderActive?: (m: IHeaderMenuItem, path: string) => string;
  };
  downloadBook?: IMenuDownloadBook;
};

export type IDesktopHeaderProps = HTMLAttributes<HTMLDivElement> & {
  mainMenu?: (IHeaderMenuItem | undefined)[];
};

const DesktopHeader: FC<IDesktopHeaderProps> = (props) => {
  const { mainMenu, className, ...other } = props;
  const [hoverMenu, setHoverMenu] = useState<string>("");

  return (
    <div
      className={clsx(
        "mx-auto flex h-full min-h-[60px] w-full max-w-screen-xl items-center gap-4",
        className,
      )}
      {...other}
    >
      {/* Three-zone layout: logo (left) | menu (centered in remaining
          space) | CTA (right). The menu uses flex-1 + justify-center so it
          sits visually centered between the logo and CTA regardless of
          either's width. */}
      <Link
        href="/"
        aria-label="Certbar Security home"
        className="inline-flex flex-shrink-0 items-center"
        prefetch={true}
      >
        <Image
          src={logo.src}
          className="mr-3 h-6 sm:h-9"
          alt="Certbar Security"
          width={150}
          height={35}
        />
      </Link>

      {mainMenu && mainMenu.length > 0 && (
        <ul className="flex h-full flex-1 items-center justify-center gap-1 font-bricolage-grotesque text-base font-normal lg:gap-2 2xl:gap-4">
          {mainMenu.map(
            (menu, idx) =>
              menu && (
                <li key={idx} className="list-none">
                  {menu.options?.width === "full" ? (
                    <MegaMenuDropdown
                      data={menu}
                      hoverMenu={hoverMenu}
                      setHoverMenu={setHoverMenu}
                    />
                  ) : (
                    <MenuDropdown
                      data={menu}
                      hoverMenu={hoverMenu}
                      setHoverMenu={setHoverMenu}
                    />
                  )}
                </li>
              ),
          )}
        </ul>
      )}

      <CTAButton
        id="get-consultancy-desktop-header-button"
        variant="orange"
        size="md"
        showIcon={true}
        iconType="arrow"
        iconPosition="right"
        className="ml-auto flex-shrink-0"
        href="/contact-us"
      >
        Book a Discovery Call
      </CTAButton>
    </div>
  );
};

export default DesktopHeader;
